// Arsenal Preventive Sensors Theme Configuration
import { createTheme } from '@mui/material/styles';

// Arsenal brand colors
const arsenalColors = {
  primary: {
    main: '#d32f2f',      // Arsenal red
    light: '#ff5722',     // Arsenal red light
    dark: '#b71c1c',      // Arsenal red dark
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#1a1a1a',      // Arsenal black
    light: '#424242',     // Arsenal gray
    dark: '#000000',
    contrastText: '#ffffff',
  },
  background: {
    default: '#f5f5f5',   // Arsenal light gray
    paper: '#ffffff',
  },
  text: {
    primary: '#1a1a1a',
    secondary: '#424242',
  },
  error: {
    main: '#d32f2f',
  },
  warning: {
    main: '#ff9800',
  },
  info: {
    main: '#2196f3',
  },
  success: {
    main: '#4caf50',
  },
};

// Create theme
const arsenalTheme = createTheme({
  palette: arsenalColors,
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      color: arsenalColors.text.primary,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
      color: arsenalColors.text.primary,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
      color: arsenalColors.text.primary,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      color: arsenalColors.text.primary,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      color: arsenalColors.text.primary,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
      color: arsenalColors.text.primary,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
      color: arsenalColors.text.secondary,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: arsenalColors.text.secondary,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: arsenalColors.text.primary,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: arsenalColors.text.secondary,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  components: {
    // Button customization
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${arsenalColors.primary.main} 0%, ${arsenalColors.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${arsenalColors.primary.dark} 0%, ${arsenalColors.primary.main} 100%)`,
          },
        },
        outlinedPrimary: {
          borderColor: arsenalColors.primary.main,
          '&:hover': {
            backgroundColor: `${arsenalColors.primary.main}10`,
          },
        },
      },
    },
    // Card customization
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
          border: 'none',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(211, 47, 47, 0.1)',
          },
        },
      },
    },
    // Paper customization
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        elevation3: {
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    // AppBar customization
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: arsenalColors.primary.main,
          background: `linear-gradient(135deg, ${arsenalColors.primary.main} 0%, ${arsenalColors.primary.dark} 100%)`,
          boxShadow: '0 2px 12px rgba(211, 47, 47, 0.3)',
        },
      },
    },
    // Drawer customization
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: arsenalColors.background.default,
          borderRight: `1px solid ${arsenalColors.primary.main}20`,
        },
      },
    },
    // Table customization
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: arsenalColors.primary.main,
          '& .MuiTableCell-head': {
            color: arsenalColors.primary.contrastText,
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: `${arsenalColors.primary.main}08`,
          },
        },
      },
    },
    // Chip customization
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 600,
        },
        colorPrimary: {
          backgroundColor: arsenalColors.primary.main,
          color: arsenalColors.primary.contrastText,
        },
      },
    },
    // TextField customization
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: arsenalColors.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: arsenalColors.primary.main,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: arsenalColors.primary.main,
          },
        },
      },
    },
    // Select customization
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    // Dialog customization
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    // Alert customization
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.MuiAlert-filledError': {
            backgroundColor: arsenalColors.primary.main,
          },
        },
      },
    },
    // Tab customization
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: arsenalColors.primary.main,
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          '&.Mui-selected': {
            color: arsenalColors.primary.main,
          },
        },
      },
    },
    // Breadcrumbs customization
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          color: arsenalColors.text.secondary,
        },
      },
    },
    // Linear Progress customization
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: `${arsenalColors.primary.main}20`,
        },
        bar: {
          borderRadius: 4,
          backgroundColor: arsenalColors.primary.main,
        },
      },
    },
    // Switch customization
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: arsenalColors.primary.main,
            '& + .MuiSwitch-track': {
              backgroundColor: arsenalColors.primary.main,
            },
          },
        },
      },
    },
    // Checkbox customization
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: arsenalColors.primary.main,
          },
        },
      },
    },
    // Radio customization
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: arsenalColors.primary.main,
          },
        },
      },
    },
    // Fab customization
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)',
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(0, 0, 0, 0.1)',
    '0 4px 12px rgba(0, 0, 0, 0.1)',
    '0 6px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 20px rgba(0, 0, 0, 0.1)',
    '0 10px 24px rgba(0, 0, 0, 0.1)',
    '0 12px 28px rgba(0, 0, 0, 0.1)',
    '0 14px 32px rgba(0, 0, 0, 0.1)',
    '0 16px 36px rgba(0, 0, 0, 0.1)',
    '0 18px 40px rgba(0, 0, 0, 0.1)',
    '0 20px 44px rgba(0, 0, 0, 0.1)',
    '0 22px 48px rgba(0, 0, 0, 0.1)',
    '0 24px 52px rgba(0, 0, 0, 0.1)',
    '0 26px 56px rgba(0, 0, 0, 0.1)',
    '0 28px 60px rgba(0, 0, 0, 0.1)',
    '0 30px 64px rgba(0, 0, 0, 0.1)',
    '0 32px 68px rgba(0, 0, 0, 0.1)',
    '0 34px 72px rgba(0, 0, 0, 0.1)',
    '0 36px 76px rgba(0, 0, 0, 0.1)',
    '0 38px 80px rgba(0, 0, 0, 0.1)',
    '0 40px 84px rgba(0, 0, 0, 0.1)',
    '0 42px 88px rgba(0, 0, 0, 0.1)',
    '0 44px 92px rgba(0, 0, 0, 0.1)',
    '0 46px 96px rgba(0, 0, 0, 0.1)',
    '0 48px 100px rgba(0, 0, 0, 0.1)',
  ],
});

// Arsenal theme variants
export const arsenalDarkTheme = createTheme({
  ...arsenalTheme,
  palette: {
    ...arsenalTheme.palette,
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
  },
});

// Export default theme
export default arsenalTheme;